import { useState } from "react";
import { Info24Filled } from "@fluentui/react-icons";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

interface PageInfoButtonProps {
  title: string;
  description?: string;
}

const pageInfo: Record<string, { title: string; description: string }> = {
  dashboard: {
    title: "Dashboard",
    description:
      "Your personal command center. The Dashboard gives you a real-time snapshot of your activity across CompBox — features completed, tasks closed, AI sessions run, and decisions logged. Use the interval selector to view daily, weekly, or monthly trends, and switch scopes to see activity across different teams. Click any metric card to drill into the details.",
  },
  requests: {
    title: "Epic Requests",
    description:
      "Epic Requests are proposals for new work — think of them as pitches before they become planned epics. Anyone can submit a request describing a problem, proposed solution, and expected impact. All requests start with 'pending' status and require approval before planning can begin. Once approved, a request can be converted into a full epic with features and tasks. Use the AI-powered Request Formulator (via CLI) for guided request creation.",
  },
  epics: {
    title: "Epics",
    description:
      "Epics are the top-level units of work in CompBox. Each epic represents a significant body of work — a feature, initiative, or project — broken down into features and tasks. Epics carry rich descriptions, execution metadata, and AI context that enables intelligent planning and autonomous implementation. Use scope filters to view epics across teams or focus on a specific team. Each epic card shows progress, feature count, and current status at a glance.",
  },
  teams: {
    title: "Teams & Products",
    description:
      "Teams and Products organize people and work in CompBox. Teams are discipline-level groups (e.g., Engineering) that own epics and requests. Products are product-level child teams (e.g., CompBox, Shiple) that inherit their parent discipline's workflow and members. Each team or product has its own epics, workflow statuses, and member roles.",
  },
  "teams & products": {
    title: "Teams & Products",
    description:
      "Teams and Products organize people and work in CompBox. Teams are discipline-level groups (e.g., Engineering) that own epics and requests. Products are product-level child teams (e.g., CompBox, Shiple) that inherit their parent discipline's workflow and members. Each team or product has its own epics, workflow statuses, and member roles.",
  },
  settings: {
    title: "Settings",
    description:
      "Manage your CompBox account settings. Update your profile information, configure your preferred timezone, manage notification preferences, and view your API tokens for CLI and MCP integrations. Admins can also access team management and user administration from here.",
  },
  admin: {
    title: "User Management",
    description:
      "Admin-only area for managing all CompBox users. View, invite, edit, and deactivate user accounts. Assign global admin privileges and manage team memberships. User activity and last login timestamps help identify inactive accounts.",
  },
};

export function PageInfoButton({ title, description }: PageInfoButtonProps) {
  const [open, setOpen] = useState(false);
  const key = title.toLowerCase();

  return (
    <>
      <button
        onClick={() => { setOpen(true); }}
        className="inline-flex items-center justify-center text-neutral-600 hover:text-foreground transition-colors"
        aria-label={`Learn about ${title}`}
      >
        <Info24Filled className="h-4 w-4" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{pageInfo[key]?.title ?? title}</DialogTitle>
            <DialogDescription className="text-sm leading-relaxed pt-2">
              {description ?? pageInfo[key]?.description ??
                `Learn more about the ${title} page.`}
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
}

export { pageInfo };
